export interface SyncResult<T> {
  deleted: string[];
  created: T[];
  updated: T[];
  failed: { item: unknown; error: unknown }[];
}

export async function syncAssignments<T extends { id?: string }>(opts: {
  toDelete: string[];
  toAdd: T[];
  toUpdate: { id: string; patch: Partial<T> }[];
  deleteOne: (id: string) => Promise<void>;
  createOne: (item: T) => Promise<T>;
  updateOne: (id: string, patch: Partial<T>) => Promise<T>;
}): Promise<SyncResult<T>> {
  const { toDelete, toAdd, toUpdate, deleteOne, createOne, updateOne } = opts;

  const deleted: string[] = [];
  const created: T[] = [];
  const updated: T[] = [];
  const failed: { item: unknown; error: unknown }[] = [];

  if (toDelete.length > 0) {
    const results = await Promise.allSettled(toDelete.map(id => deleteOne(id)));
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        deleted.push(toDelete[i]);
      } else {
        failed.push({ item: toDelete[i], error: result.reason });
      }
    });
  }

  for (const item of toAdd) {
    if (item.id) {
      created.push(item);
      continue;
    }
    try {
      created.push(await createOne(item));
    } catch (error) {
      failed.push({ item, error });
    }
  }

  for (const { id, patch } of toUpdate) {
    try {
      updated.push(await updateOne(id, patch));
    } catch (error) {
      failed.push({ item: { id, patch }, error });
    }
  }

  return { deleted, created, updated, failed };
}
